import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, ExternalLink } from "lucide-react";
import { ModalScroll } from "@/components/public/ModalScroll";

/**
 * DetailModal — shared modal for material & certification list pages.
 * - Auto-shrinks to its content (no blank space when content is small).
 * - Caps at 90vh and shows the custom (non-native) scrollbar when content overflows.
 */
export function DetailModal({
  open,
  onClose,
  title,
  subtitle,
  badge,
  externalLink,
  fullPageHref,
  testId = "detail-modal",
  children,
}) {
  const bodyRef = useRef(null);
  const panelRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Measure if content + chrome exceeds 90vh; if so, cap the panel at 90vh
  // so the body becomes scrollable. Otherwise let the panel size to content.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const panel = panelRef.current;
      const body = bodyRef.current;
      if (!panel || !body) return;
      const chrome = panel.offsetHeight - body.offsetHeight;
      const cap = Math.floor(window.innerHeight * 0.9);
      const needed = chrome + body.scrollHeight;
      setOverflowing(needed > cap);
    };
    measure();
    window.addEventListener("resize", measure);
    const ro = new ResizeObserver(measure);
    if (bodyRef.current) ro.observe(bodyRef.current);
    return () => {
      window.removeEventListener("resize", measure);
      ro.disconnect();
    };
  }, [open, children]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid={testId}
    >
      <div
        ref={panelRef}
        className={`bg-ivory border border-line shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden ${overflowing ? "h-[90vh]" : ""}`}
      >
        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 pb-3 border-b border-line shrink-0">
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-serif text-2xl min-w-0 truncate" title={typeof title === "string" ? title : undefined}>{title}</h2>
            <div className="flex items-center gap-3 shrink-0">
              {badge}
              <button
                type="button"
                onClick={onClose}
                className="text-muted_text hover:text-ink transition-colors p-1"
                aria-label="Close"
                data-testid={`${testId}-close`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          {subtitle && <div className="text-xs text-muted_text mt-1">{subtitle}</div>}
        </div>

        {/* Body — scrolls only when overflowing; otherwise sizes to content */}
        {overflowing ? (
          <ModalScroll className="px-6 sm:px-8 py-5" testId={`${testId}-body`}>
            <div ref={bodyRef}>{children}</div>
          </ModalScroll>
        ) : (
          <div ref={bodyRef} className="px-6 sm:px-8 py-5">{children}</div>
        )}

        {/* Footer */}
        <div className="border-t border-line px-6 sm:px-8 py-3 flex flex-wrap items-center justify-between gap-3 shrink-0">
          {externalLink ? (
            <a
              href={externalLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm inline-flex items-center gap-1 text-forest hover:underline"
              data-testid={`${testId}-external`}
            >
              {externalLink.label} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : <span />}
          {fullPageHref && (
            <Link to={fullPageHref} className="text-sm text-forest underline" data-testid={`${testId}-full-page`}>
              View full page →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/** Section helper used by both modals. */
export function DetailModalSection({ title, children }) {
  if (!children) return null;
  return (
    <div>
      <h3 className="font-serif text-base mb-1">{title}</h3>
      <p className="text-sm text-muted_text leading-relaxed whitespace-pre-line">{children}</p>
    </div>
  );
}
